import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { socketService } from "../../../services/socket";
import { CustomDialog } from "../components/CustomDialog";
import { CustomSelect } from "../components/CustomSelect";

interface StaffMember {
  id: string;
  name: string;
  role: string;
  password?: string;
  image?: string;
}

interface RolesEditorProps {
  externalStaff?: StaffMember[];
  activeMembers?: { id: string; name: string }[];
}

export const RolesEditor: React.FC<RolesEditorProps> = ({
  externalStaff = [],
  activeMembers = [],
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeProjectId = searchParams.get("projectId");
  const _staff = externalStaff;

  // Helper to check if a member is online
  const isOnline = (name: string) => {
    if (!name || !activeMembers) return false;
    return activeMembers.some((m) => m?.name?.toUpperCase() === name.toUpperCase());
  };

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [usersList, setUsersList] = useState<any[]>([]);
  const [projectsList, setProjectsList] = useState<{ id: string; name: string }[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);

  // Form State
  const [formName, setFormName] = useState("");
  const [formRole, setFormRole] = useState("Logger");
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formImage, setFormImage] = useState("");
  const [formProject, setFormProject] = useState("");

  const rolesAvailable = [
    "Executive",
    "Showrunner",
    "Content Controller",
    "Content Director",
    "Story Producer",
    "Editor",
    "Logger",
    "Cliente",
    "Admin",
    "Superadmin",
  ];

  const getRoleColor = (role: string) => {
    const r = (role || "").toLowerCase();
    if (r === "superadmin") return "var(--color-accent-danger)";
    if (r === "admin") return "var(--color-data-transcript)";
    if (r.includes("showrunner")) return "var(--color-role-showrunner)";
    if (r.includes("log")) return "var(--color-data-log)";
    if (r.includes("story")) return "var(--color-data-story)";
    if (r.includes("content director")) return "var(--color-role-contentdirector)";
    if (r.includes("content")) return "var(--color-data-event)";
    if (r.includes("executive")) return "var(--color-accent-warning)";
    if (r.includes("editor")) return "var(--color-accent-success)";
    if (r === "cliente") return "var(--color-role-cliente)";
    return "var(--color-text-secondary)";
  };

  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "alert" | "confirm";
    onConfirm: (val?: string) => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "alert",
    onConfirm: () => {},
  });

  const loadUsers = () => {
    setIsLoading(true);
    socketService.emit("admin:get-users");
  };

  useEffect(() => {
    socketService.connect();

    const handleUsersData = (data: any) => {
      setIsLoading(false);
      if (data.success && data.users) {
        setUsersList(data.users);
        if (data.projects) setProjectsList(data.projects);
      }
    };

    const handleUserAction = (data: any) => {
      setIsLoading(false);
      if (data.success) {
        setIsAdding(false);
        setEditingId(null);
        resetForm();
        setErrorMsg("");
        loadUsers(); // refresh list
      } else {
        setErrorMsg(data.error || t("controller.roles.actionFailed"));
      }
    };

    const handleBulkAction = (data: any) => {
      setIsBulkActionLoading(false);
      if (data.success) {
        setSelectedUsers([]);
        loadUsers(); // refresh list
      } else {
        setErrorMsg(data.error || t("controller.roles.bulkActionFailed"));
      }
    };

    // Re-fetch users whenever socket (re)connects
    const handleConnect = () => {
      loadUsers();
    };

    // Handle permission denied (requireSocketRole emits generic "error")
    const handleSocketError = (data: any) => {
      if (data?.event === "unauthorized") {
        setIsLoading(false);
        setErrorMsg(data.message || t("controller.roles.actionFailed"));
      }
    };

    socketService.on("admin:users-data", handleUsersData);
    socketService.on("admin:user-created", handleUserAction);
    socketService.on("admin:user-updated", handleUserAction);
    socketService.on("admin:user-deleted", handleUserAction);
    socketService.on("admin:users-bulk-deleted", handleBulkAction);
    socketService.on("admin:users-role-bulk-updated", handleBulkAction);
    socketService.on("connect", handleConnect);
    socketService.on("error", handleSocketError);

    // Load immediately only if already connected; otherwise wait for "connect" event
    if (socketService.connected) {
      loadUsers();
    }

    return () => {
      socketService.off("admin:users-data", handleUsersData);
      socketService.off("admin:user-created", handleUserAction);
      socketService.off("admin:user-updated", handleUserAction);
      socketService.off("admin:user-deleted", handleUserAction);
      socketService.off("admin:users-bulk-deleted", handleBulkAction);
      socketService.off("admin:users-role-bulk-updated", handleBulkAction);
      socketService.off("connect", handleConnect);
      socketService.off("error", handleSocketError);
    };
  }, []);

  const resetForm = () => {
    setFormName("");
    setFormEmail("");
    setFormRole("Logger");
    setFormPassword("");
    setFormImage("");
    setFormProject("");
    setErrorMsg("");
  };

  const handleSave = () => {
    if (!formName || !formEmail) {
      setErrorMsg(t("controller.roles.nameEmailRequired"));
      return;
    }
    // Password is now optional — if blank, user will use Google OAuth

    setIsLoading(true);
    if (isAdding) {
      socketService.emit("admin:create-user", {
        email: formEmail,
        password: formPassword,
        name: formName,
        role: formRole,
        image: formImage || undefined,
        projectId: formProject || undefined,
      });
    } else if (editingId) {
      socketService.emit("admin:update-user", {
        id: editingId,
        name: formName,
        role: formRole,
        password: formPassword,
        image: formImage || undefined,
        projectId: formProject || null,
      });
    }
  };

  const toggleUserSelection = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id],
    );
  };

  const handleBulkDelete = () => {
    if (selectedUsers.length === 0) return;
    setDialog({
      isOpen: true,
      title: t("controller.roles.removeMultipleStaff"),
      message: t("controller.roles.removeMultipleStaffConfirm", { count: selectedUsers.length }),
      type: "confirm",
      onConfirm: () => {
        setIsBulkActionLoading(true);
        socketService.emit("admin:delete-users-bulk", { ids: selectedUsers });
        setDialog((d) => ({ ...d, isOpen: false }));
      },
    });
  };

  const handleBulkRoleChange = (newRole: string) => {
    if (selectedUsers.length === 0 || !newRole) return;
    setIsBulkActionLoading(true);
    socketService.emit("admin:update-users-role-bulk", { ids: selectedUsers, role: newRole });
  };

  const handleDelete = (id: string) => {
    setDialog({
      isOpen: true,
      title: t("controller.roles.removeStaff"),
      message: t("controller.roles.removeStaffConfirm"),
      type: "confirm",
      onConfirm: () => {
        setIsLoading(true);
        socketService.emit("admin:delete-user", { id });
        setDialog((d) => ({ ...d, isOpen: false }));
      },
    });
  };

  const startEdit = (member: any) => {
    setEditingId(member.id);
    setIsAdding(false);
    setFormName(member.name);
    setFormEmail(member.email);
    setFormRole(member.app_role || "Logger");
    setFormPassword("");
    setFormImage(member.image || "");
    setFormProject(member.project_id || "");

    // Scroll to the top to make the form visible
    setTimeout(() => {
      document
        .getElementById("roles-editor-top")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setFormImage(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div
      id="roles-editor-top"
      data-testid="roles-editor"
      style={{
        padding: "40px",
        color: "rgba(255, 255, 255, 0.8)",
        minHeight: "100%",
        background: "var(--color-panel-bg)",
        borderRadius: "var(--border-radius-lg)",
        border: "1px solid var(--color-panel-border)",
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "40px",
          borderBottom: "1px solid #333",
          paddingBottom: "20px",
        }}
      >
        <div>
          <h2
            style={{
              color: "var(--color-accent-danger)",
              margin: 0,
              fontSize: "1.5rem",
              fontWeight: "900",
              letterSpacing: "2px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {activeProjectId && (
              <button
                onClick={() => setSearchParams({ view: "projects" })}
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  color: "var(--color-text-secondary)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "6px",
                  borderRadius: "8px",
                  transition: "all 0.2s",
                }}
                title={t("admin.layout.backToProjects") || "Back to projects"}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                  e.currentTarget.style.color = "var(--color-text-secondary)";
                }}
              >
                <i className="bx bx-arrow-back" style={{ fontSize: "1.2rem" }}></i>
              </button>
            )}
            {t("controller.roles.rolesPermissions")}
          </h2>
          <p style={{ color: "#888", margin: "5px 0 0 0", fontSize: "0.9rem" }}>
            {t("controller.roles.defineSystemUsers")}
          </p>
        </div>
        {!isAdding && !editingId && (
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              data-testid="roles-add-staff-btn"
              onClick={() => {
                setIsAdding(true);
                setEditingId(null);
                resetForm();
                if (activeProjectId) {
                  setFormProject(activeProjectId);
                }
                setTimeout(() => {
                  document
                    .getElementById("roles-editor-top")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 50);
              }}
              style={{
                background: "var(--color-accent-success)",
                color: "#000",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                fontWeight: "bold",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {t("controller.roles.addStaffMember")}
            </button>
          </div>
        )}
      </div>

      {(isAdding || editingId) && (
        <div
          data-testid="roles-form"
          style={{
            background: "rgba(255,255,255,0.03)",
            padding: "30px",
            borderRadius: "16px",
            border: "1px solid #444",
            marginBottom: "40px",
            display: "flex",
            gap: "40px",
            animation: "fadeIn 0.3s ease-out",
          }}
        >
          <div
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "15px" }}
          >
            <div
              style={{
                width: "120px",
                height: "120px",
                aspectRatio: "1 / 1",
                flexShrink: 0,
                borderRadius: "50%",
                background: `url(${formImage || `https://ui-avatars.com/api/?name=${encodeURIComponent(formName || "U")}&background=random&color=fff`}) center/cover no-repeat`,
                border: "2px solid #555",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.8rem",
                fontWeight: "bold",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 0 10px rgba(0,0,0,0.5)",
              }}
              onClick={() => document.getElementById("staff-image-upload")?.click()}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "rgba(0,0,0,0.6)",
                  color: "rgba(255, 255, 255, 0.8)",
                  fontSize: "0.6rem",
                  textAlign: "center",
                  padding: "4px",
                }}
              >
                EDIT
              </div>
            </div>
            <input id="staff-image-upload" type="file" hidden onChange={handleImageUpload} />
          </div>

          <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div>
              <label
                style={{
                  display: "block",
                  color: "#888",
                  fontSize: "0.7rem",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                {t("controller.roles.fullName")}
              </label>
              <input
                data-testid="roles-form-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                style={{
                  width: "100%",
                  background: "#111",
                  border: "1px solid #333",
                  color: "rgba(255, 255, 255, 0.8)",
                  padding: "12px",
                  borderRadius: "8px",
                  outline: "none",
                }}
                placeholder={t("controller.roles.enterName")}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  color: "#888",
                  fontSize: "0.7rem",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                {t("controller.roles.workEmail")}
              </label>
              <input
                data-testid="roles-form-email"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                disabled={!!editingId}
                style={{
                  width: "100%",
                  background: editingId ? "#333" : "#111",
                  border: "1px solid #333",
                  color: "rgba(255, 255, 255, 0.8)",
                  padding: "12px",
                  borderRadius: "8px",
                  outline: "none",
                }}
                placeholder="name@braindog.ai"
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  color: "#888",
                  fontSize: "0.7rem",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                {t("controller.roles.systemRole")}
              </label>
              <div style={{ position: "relative" }}>
                <div
                  data-testid="roles-form-role-selector"
                  onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
                  style={{
                    width: "100%",
                    background: "#111",
                    border: "1px solid #333",
                    color: "rgba(255, 255, 255, 0.8)",
                    padding: "12px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxSizing: "border-box",
                  }}
                >
                  <span style={{ color: getRoleColor(formRole), fontWeight: "bold" }}>
                    {formRole}
                  </span>
                  <i
                    className={`bx bx-chevron-${roleDropdownOpen ? "up" : "down"}`}
                    style={{ fontSize: "1.2rem", color: "#666" }}
                  ></i>
                </div>
                {roleDropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 50,
                      background: "#1a1a1a",
                      border: "1px solid #444",
                      borderRadius: "8px",
                      marginTop: "4px",
                      overflow: "hidden",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                    }}
                  >
                    {rolesAvailable.map((r) => (
                      <div
                        key={r}
                        onClick={() => {
                          setFormRole(r);
                          setRoleDropdownOpen(false);
                        }}
                        style={{
                          padding: "12px 16px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          background: formRole === r ? "rgba(255,255,255,0.05)" : "transparent",
                          borderLeft:
                            formRole === r
                              ? `3px solid ${getRoleColor(r)}`
                              : "3px solid transparent",
                          transition: "all 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            formRole === r ? "rgba(255,255,255,0.05)" : "transparent";
                        }}
                      >
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: getRoleColor(r),
                          }}
                        ></div>
                        <span
                          style={{
                            color: getRoleColor(r),
                            fontWeight: formRole === r ? "bold" : "normal",
                            fontSize: "0.9rem",
                          }}
                        >
                          {r}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  color: "#888",
                  fontSize: "0.7rem",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                ACCESS PASSWORD{" "}
                {isAdding ? "(Leave empty for Google OAuth)" : "(Leave empty if unchanged)"}
              </label>
              <input
                data-testid="roles-form-password"
                type="text"
                value={formPassword}
                onChange={(e) => setFormPassword(e.target.value)}
                style={{
                  width: "100%",
                  background: "#111",
                  border: "1px solid #333",
                  color: "rgba(255, 255, 255, 0.8)",
                  padding: "12px",
                  borderRadius: "8px",
                  outline: "none",
                }}
                placeholder={t("controller.roles.setPassword")}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  color: "#888",
                  fontSize: "0.7rem",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                {t("controller.roles.assignedProject")}
              </label>
              <div style={{ position: "relative" }}>
                <div
                  data-testid="roles-form-project-selector"
                  onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                  style={{
                    width: "100%",
                    background: "#111",
                    border: "1px solid #333",
                    color: formProject ? "rgba(255, 255, 255, 0.8)" : "#666",
                    padding: "12px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxSizing: "border-box",
                  }}
                >
                  <span
                    style={{
                      fontWeight: formProject ? "bold" : "normal",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {formProject === "ALL" ? (
                      <>
                        <i className="bx bx-globe"></i> {t("controller.roles.allProjects")}
                      </>
                    ) : formProject ? (
                      projectsList.find((p) => p.id === formProject)?.name || formProject
                    ) : (
                      t("controller.roles.noProject")
                    )}
                  </span>
                  <i
                    className={`bx bx-chevron-${projectDropdownOpen ? "up" : "down"}`}
                    style={{ fontSize: "1.2rem", color: "#666" }}
                  ></i>
                </div>
                {projectDropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 50,
                      background: "#1a1a1a",
                      border: "1px solid #444",
                      borderRadius: "8px",
                      marginTop: "4px",
                      overflow: "hidden",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                    }}
                  >
                    <div
                      onClick={() => {
                        setFormProject("");
                        setProjectDropdownOpen(false);
                      }}
                      style={{
                        padding: "12px 16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        background: !formProject ? "rgba(255,255,255,0.05)" : "transparent",
                        borderLeft: !formProject ? "3px solid #888" : "3px solid transparent",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = !formProject
                          ? "rgba(255,255,255,0.05)"
                          : "transparent";
                      }}
                    >
                      <i
                        className="bx bx-x-circle"
                        style={{ fontSize: "0.9rem", color: "#888" }}
                      ></i>
                      <span style={{ color: "#888", fontSize: "0.9rem" }}>
                        {t("controller.roles.noProject")}
                      </span>
                    </div>
                    <div
                      onClick={() => {
                        setFormProject("ALL");
                        setProjectDropdownOpen(false);
                      }}
                      style={{
                        padding: "12px 16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        background:
                          formProject === "ALL" ? "rgba(139, 92, 246, 0.1)" : "transparent",
                        borderLeft:
                          formProject === "ALL" ? "3px solid #A855F7" : "3px solid transparent",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(139, 92, 246, 0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          formProject === "ALL" ? "rgba(139, 92, 246, 0.1)" : "transparent";
                      }}
                    >
                      <i
                        className="bx bx-globe"
                        style={{ fontSize: "0.9rem", color: "#A855F7" }}
                      ></i>
                      <span
                        style={{
                          color: "#A855F7",
                          fontWeight: formProject === "ALL" ? "bold" : "normal",
                          fontSize: "0.9rem",
                        }}
                      >
                        {t("controller.roles.allProjects")}
                      </span>
                    </div>
                    {projectsList.map((p) => (
                      <div
                        key={p.id}
                        onClick={() => {
                          setFormProject(p.id);
                          setProjectDropdownOpen(false);
                        }}
                        style={{
                          padding: "12px 16px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          background:
                            formProject === p.id ? "rgba(255,255,255,0.05)" : "transparent",
                          borderLeft:
                            formProject === p.id
                              ? "3px solid var(--color-accent-primary)"
                              : "3px solid transparent",
                          transition: "all 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            formProject === p.id ? "rgba(255,255,255,0.05)" : "transparent";
                        }}
                      >
                        <i
                          className="bx bx-folder"
                          style={{ fontSize: "0.9rem", color: "var(--color-accent-primary)" }}
                        ></i>
                        <span
                          style={{
                            color: "rgba(255,255,255,0.8)",
                            fontWeight: formProject === p.id ? "bold" : "normal",
                            fontSize: "0.9rem",
                          }}
                        >
                          {p.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {errorMsg && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  color: "#EF4444",
                  fontSize: "0.8rem",
                  background: "rgba(239,68,68,0.1)",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              >
                <i className="bx bx-error" style={{ marginRight: "5px" }}></i> {errorMsg}
              </div>
            )}
            <div
              style={{ display: "flex", gap: "15px", alignItems: "flex-end", gridColumn: "1 / -1" }}
            >
              <button
                data-testid="roles-form-save-btn"
                onClick={handleSave}
                disabled={isLoading}
                style={{
                  flex: 1,
                  background: "var(--color-accent-primary)",
                  color: "rgba(255, 255, 255, 0.8)",
                  border: "none",
                  padding: "12px",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  cursor: isLoading ? "wait" : "pointer",
                  opacity: isLoading ? 0.7 : 1,
                }}
              >
                {isLoading
                  ? t("controller.roles.savingAccount")
                  : isAdding
                    ? t("controller.roles.createAccount")
                    : t("controller.roles.updateAccount")}
              </button>
              <button
                data-testid="roles-form-cancel-btn"
                onClick={() => {
                  setIsAdding(false);
                  setEditingId(null);
                  resetForm();
                }}
                style={{
                  background: "transparent",
                  border: "1px solid #444",
                  color: "#888",
                  padding: "12px 20px",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                {t("controller.dashboard.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedUsers.length > 0 && (
        <div
          data-testid="roles-bulk-actions"
          style={{
            background: "rgba(247,45,96,0.1)",
            border: "1px solid var(--color-accent-danger)",
            borderRadius: "8px",
            padding: "15px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "25px",
            animation: "fadeIn 0.2s ease-out",
          }}
        >
          <div style={{ fontWeight: "bold", color: "rgba(255, 255, 255, 0.9)" }}>
            {t("controller.roles.usersSelected", { count: selectedUsers.length })}
          </div>
          <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
            <CustomSelect
              data-testid="roles-bulk-change-role"
              value=""
              onChange={(v) => {
                if (v !== "") {
                  handleBulkRoleChange(v);
                }
              }}
              options={rolesAvailable.map((r) => ({ value: r, label: r }))}
              placeholder={t("controller.roles.changeRole")}
              style={{ minWidth: "150px" }}
            />
            <button
              data-testid="roles-bulk-delete-btn"
              onClick={handleBulkDelete}
              disabled={isBulkActionLoading}
              style={{
                background: "var(--color-accent-danger)",
                color: "#fff",
                border: "none",
                padding: "8px 20px",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              {t("controller.roles.deleteSelected")}
            </button>
            <button
              data-testid="roles-bulk-clear-btn"
              onClick={() => setSelectedUsers([])}
              style={{
                background: "transparent",
                border: "none",
                color: "#888",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {t("controller.roles.clear")}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "30px" }}>
        {rolesAvailable.map((roleName) => {
          const filteredList = activeProjectId
            ? usersList.filter(
                (u) =>
                  u.project_id === activeProjectId || u.project_id === null || u.project_id === "",
              )
            : usersList;
          const usersInRole = filteredList.filter(
            (u) => (u.app_role || "").trim().toLowerCase() === roleName.toLowerCase(),
          );
          if (usersInRole.length === 0) return null;

          return (
            <div
              key={roleName}
              data-testid={`roles-group-${roleName.toLowerCase().replace(/\s+/g, "-")}`}
              style={{
                background: "rgba(0,0,0,0.2)",
                border: `1px solid ${getRoleColor(roleName).replace(")", ", 0.2)").replace("var", "rgba")}`, // Fallback if var not parsable, handled by CSS mostly though. We'll stick to a simple border
                borderColor: getRoleColor(roleName),
                borderRadius: "16px",
                padding: "20px",
              }}
            >
              <h2
                style={{
                  color: getRoleColor(roleName),
                  marginTop: 0,
                  marginBottom: "20px",
                  fontSize: "1.2rem",
                  borderBottom: `1px solid ${getRoleColor(roleName)}`,
                  paddingBottom: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "2px",
                }}
              >
                <i className="bx bx-group"></i> {roleName}S
                <span
                  style={{
                    fontSize: "0.7rem",
                    background: "rgba(255,255,255,0.1)",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    color: "#fff",
                  }}
                >
                  {usersInRole.length}
                </span>
              </h2>

              <div
                data-testid={`roles-group-${roleName.toLowerCase().replace(/\s+/g, "-")}-list`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: "20px",
                }}
              >
                {usersInRole.map((member) => (
                  <div
                    key={member.id}
                    data-testid={`roles-user-card-${member.id}`}
                    style={{
                      background: selectedUsers.includes(member.id)
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(255,255,255,0.02)",
                      border: selectedUsers.includes(member.id)
                        ? `1px solid ${getRoleColor(member.app_role)}`
                        : "1px solid #222",
                      borderRadius: "20px",
                      padding: "25px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "15px",
                      position: "relative",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                    onClick={(e) => toggleUserSelection(member.id, e)}
                    onMouseEnter={(e) => {
                      if (!selectedUsers.includes(member.id)) {
                        e.currentTarget.style.borderColor = getRoleColor(member.app_role);
                        e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedUsers.includes(member.id)) {
                        e.currentTarget.style.borderColor = "#222";
                        e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                      }
                    }}
                  >
                    {/* Checkbox indicator */}
                    <div style={{ position: "absolute", top: "20px", left: "20px" }}>
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "4px",
                          border: selectedUsers.includes(member.id) ? "none" : "2px solid #555",
                          background: selectedUsers.includes(member.id)
                            ? getRoleColor(member.app_role)
                            : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {selectedUsers.includes(member.id) && (
                          <span style={{ color: "#fff", fontSize: "14px", fontWeight: "bold" }}>
                            ✓
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        width: "80px",
                        height: "80px",
                        aspectRatio: "1 / 1",
                        flexShrink: 0,
                        borderRadius: "50%",
                        background: member.image
                          ? `url(${member.image}) center/cover no-repeat`
                          : "#333",
                        border: `3px solid ${getRoleColor(member.app_role)}`,
                        boxShadow: `0 0 20px ${getRoleColor(member.app_role)}33`,
                      }}
                    />

                    <div style={{ textAlign: "center" }}>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "1.2rem",
                          color: "rgba(255, 255, 255, 0.8)",
                          fontWeight: "bold",
                        }}
                      >
                        {member.name}
                      </h3>
                      <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "5px" }}>
                        {member.email}
                      </div>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: getRoleColor(member.app_role),
                          fontWeight: "900",
                          letterSpacing: "2px",
                          textTransform: "uppercase",
                        }}
                      >
                        {member.app_role || "user"}
                      </span>
                      {(member.projects || !member.project_id) && (
                        <div
                          style={{
                            fontSize: "0.55rem",
                            color: !member.project_id ? "#A855F7" : "#666",
                            marginTop: "4px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <i
                            className={!member.project_id ? "bx bx-globe" : "bx bx-folder"}
                            style={{ fontSize: "0.7rem" }}
                          ></i>
                          {!member.project_id
                            ? t("controller.roles.allProjects")
                            : member.projects?.name}
                        </div>
                      )}
                    </div>

                    <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                      <button
                        data-testid={`roles-edit-btn-${member.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(member);
                        }}
                        style={{
                          background: "#333",
                          border: "none",
                          color: "rgba(255, 255, 255, 0.8)",
                          padding: "8px 15px",
                          borderRadius: "6px",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                          cursor: "pointer",
                        }}
                      >
                        {t("controller.roles.edit")}
                      </button>
                      <button
                        data-testid={`roles-remove-btn-${member.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(member.id);
                        }}
                        style={{
                          background: "rgba(247,45,96,0.1)",
                          border: "none",
                          color: "var(--color-accent-danger)",
                          padding: "8px 15px",
                          borderRadius: "6px",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                          cursor: "pointer",
                        }}
                      >
                        {t("controller.roles.remove")}
                      </button>
                    </div>

                    {/* Status/Badge */}
                    <div style={{ position: "absolute", top: "20px", right: "20px" }}>
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          background: isOnline(member.name) ? "#00d084" : "#444",
                          boxShadow: isOnline(member.name) ? "0 0 10px #00d084" : "none",
                          transition: "all 0.3s ease",
                        }}
                        title={
                          isOnline(member.name)
                            ? t("controller.roles.currentlyActive")
                            : t("controller.roles.offlineStatus")
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Fallback for users with unassigned or other roles */}
        {(() => {
          const knownRoles = rolesAvailable;
          const filteredList = activeProjectId
            ? usersList.filter(
                (u) =>
                  u.project_id === activeProjectId || u.project_id === null || u.project_id === "",
              )
            : usersList;
          const otherUsers = filteredList.filter(
            (u) =>
              !knownRoles
                .map((r) => r.toLowerCase())
                .includes((u.app_role || "").trim().toLowerCase()),
          );
          if (otherUsers.length === 0) return null;
          return (
            <div
              data-testid="roles-group-other"
              style={{
                background: "rgba(0,0,0,0.2)",
                border: "1px solid #333",
                borderRadius: "16px",
                padding: "20px",
              }}
            >
              <h2
                style={{
                  color: "#888",
                  marginTop: 0,
                  marginBottom: "20px",
                  fontSize: "1.2rem",
                  borderBottom: "1px solid #333",
                  paddingBottom: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "2px",
                }}
              >
                <i className="bx bx-question-mark"></i> {t("controller.roles.otherUnassigned")}
                <span
                  style={{
                    fontSize: "0.7rem",
                    background: "rgba(255,255,255,0.1)",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    color: "#fff",
                  }}
                >
                  {otherUsers.length}
                </span>
              </h2>
              <div
                data-testid="roles-group-other-list"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: "20px",
                }}
              >
                {otherUsers.map((member) => (
                  <div
                    key={member.id}
                    data-testid={`roles-user-card-${member.id}`}
                    style={{
                      background: selectedUsers.includes(member.id)
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(255,255,255,0.02)",
                      border: selectedUsers.includes(member.id)
                        ? `1px solid ${getRoleColor(member.app_role)}`
                        : "1px solid #222",
                      borderRadius: "20px",
                      padding: "25px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "15px",
                      position: "relative",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                    onClick={(e) => toggleUserSelection(member.id, e)}
                    onMouseEnter={(e) => {
                      if (!selectedUsers.includes(member.id)) {
                        e.currentTarget.style.borderColor = getRoleColor(member.app_role);
                        e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedUsers.includes(member.id)) {
                        e.currentTarget.style.borderColor = "#222";
                        e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                      }
                    }}
                  >
                    <div style={{ position: "absolute", top: "20px", left: "20px" }}>
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "4px",
                          border: selectedUsers.includes(member.id) ? "none" : "2px solid #555",
                          background: selectedUsers.includes(member.id)
                            ? getRoleColor(member.app_role)
                            : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {selectedUsers.includes(member.id) && (
                          <span style={{ color: "#fff", fontSize: "14px", fontWeight: "bold" }}>
                            ✓
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        width: "80px",
                        height: "80px",
                        aspectRatio: "1 / 1",
                        flexShrink: 0,
                        borderRadius: "50%",
                        background: member.image
                          ? `url(${member.image}) center/cover no-repeat`
                          : "#333",
                        border: `3px solid ${getRoleColor(member.app_role)}`,
                        boxShadow: `0 0 20px ${getRoleColor(member.app_role)}33`,
                      }}
                    />

                    <div style={{ textAlign: "center" }}>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "1.2rem",
                          color: "rgba(255, 255, 255, 0.8)",
                          fontWeight: "bold",
                        }}
                      >
                        {member.name}
                      </h3>
                      <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "5px" }}>
                        {member.email}
                      </div>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: getRoleColor(member.app_role),
                          fontWeight: "900",
                          letterSpacing: "2px",
                          textTransform: "uppercase",
                        }}
                      >
                        {member.app_role || "user"}
                      </span>
                      {(member.projects || !member.project_id) && (
                        <div
                          style={{
                            fontSize: "0.55rem",
                            color: !member.project_id ? "#A855F7" : "#666",
                            marginTop: "4px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <i
                            className={!member.project_id ? "bx bx-globe" : "bx bx-folder"}
                            style={{ fontSize: "0.7rem" }}
                          ></i>
                          {!member.project_id
                            ? t("controller.roles.allProjects")
                            : member.projects?.name}
                        </div>
                      )}
                    </div>

                    <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                      <button
                        data-testid={`roles-edit-btn-${member.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(member);
                        }}
                        style={{
                          background: "#333",
                          border: "none",
                          color: "rgba(255, 255, 255, 0.8)",
                          padding: "8px 15px",
                          borderRadius: "6px",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                          cursor: "pointer",
                        }}
                      >
                        {t("controller.roles.edit")}
                      </button>
                      <button
                        data-testid={`roles-remove-btn-${member.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(member.id);
                        }}
                        style={{
                          background: "rgba(247,45,96,0.1)",
                          border: "none",
                          color: "var(--color-accent-danger)",
                          padding: "8px 15px",
                          borderRadius: "6px",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                          cursor: "pointer",
                        }}
                      >
                        {t("controller.roles.remove")}
                      </button>
                    </div>

                    <div style={{ position: "absolute", top: "20px", right: "20px" }}>
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          background: isOnline(member.name) ? "#00d084" : "#444",
                          boxShadow: isOnline(member.name) ? "0 0 10px #00d084" : "none",
                          transition: "all 0.3s ease",
                        }}
                        title={
                          isOnline(member.name)
                            ? t("controller.roles.currentlyActive")
                            : t("controller.roles.offlineStatus")
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
      {isLoading && usersList.length === 0 && (
        <div style={{ gridColumn: "1 / -1", padding: "60px", textAlign: "center", color: "#666" }}>
          {t("controller.roles.loadingFromSupabase")}
        </div>
      )}

      {!isLoading &&
        (activeProjectId
          ? usersList.filter(
              (u) =>
                u.project_id === activeProjectId || u.project_id === null || u.project_id === "",
            ).length === 0
          : usersList.length === 0) &&
        !isAdding && (
          <div
            style={{
              gridColumn: "1 / -1",
              padding: "60px",
              textAlign: "center",
              color: "#666",
              border: "2px dashed #222",
              borderRadius: "20px",
            }}
          >
            <div style={{ fontSize: "3rem", marginBottom: "20px", opacity: 0.2 }}>
              <i className="bx bx-group"></i>
            </div>
            <p>No staff accounts yet. Create one to get started.</p>
            <button
              onClick={() => {
                setIsAdding(true);
                setEditingId(null);
                resetForm();
                if (activeProjectId) {
                  setFormProject(activeProjectId);
                }
              }}
              style={{
                background: "transparent",
                color: "var(--color-accent-danger)",
                border: "1px solid var(--color-accent-danger)",
                padding: "10px 20px",
                borderRadius: "8px",
                marginTop: "10px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              CREATE FIRST ACCOUNT
            </button>
          </div>
        )}

      <style>{`
                @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
      {/* Final Dialog Component */}
      <CustomDialog
        isOpen={dialog.isOpen}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        onConfirm={dialog.onConfirm}
        onCancel={() => setDialog((d) => ({ ...d, isOpen: false }))}
      />
    </div>
  );
};
